import { zodResolver } from '@hookform/resolvers/zod';
import { Channel } from 'api/ChannelsAPI';
import {
  BINDING_TYPE,
  CHANNEL_BINDINGS,
  CHANNEL_NAME,
  ERROR_TEXT,
} from 'modules/channels/constants';
import { BindingTypes, ChannelBinding, UpdateChannelFormState } from 'modules/channels/types';
import { calculatePercentage, createDefaultChannelBindings } from 'modules/channels/utils';
import { ReactNode } from 'react';
import { FormProvider, UseFormHandleSubmit, useForm } from 'react-hook-form';
import * as z from 'zod';

const validateAccounts = (bindingType: BindingTypes, channelBindings: ChannelBinding[] = []) =>
  bindingType === BindingTypes.CUSTOM ? channelBindings.every((binding) => !!binding.option) : true;

const validatePercentages = (bindingType: BindingTypes, channelBindings: ChannelBinding[] = []) =>
  bindingType === BindingTypes.CUSTOM ? calculatePercentage(channelBindings) <= 100 : true;

const schema = z
  .object({
    [BINDING_TYPE]: z.nativeEnum(BindingTypes),
    [CHANNEL_BINDINGS]: z.optional(
      z.array(
        z.object({
          id: z.string(),
          num: z.string(),
          option: z.string(),
        }),
      ),
    ),
    [CHANNEL_NAME]: z.string().trim().min(1, { message: ERROR_TEXT.NO_NAME }),
  })
  .refine((val) => validateAccounts(val.bindingType, val.channelBindings || []), {
    message: 'Please selet an option',
    path: ['channelBindings.option'],
  })
  .refine((val) => validatePercentages(val.bindingType, val.channelBindings || []), {
    message: 'Max percentage is 100',
    path: ['channelBindings.num'],
  });

export const UpdateChannelFormContext = ({
  children,
  channel,
}: {
  children: ReactNode;
  channel: Channel;
}) => {
  const { espBindings = [] } = channel;
  const defaultBindingType = espBindings.length > 0 ? BindingTypes.CUSTOM : BindingTypes.DEFAULT;
  const defaultChannelBindings = createDefaultChannelBindings(espBindings);

  const methods = useForm<UpdateChannelFormState>({
    criteriaMode: 'all',
    defaultValues: {
      bindingType: defaultBindingType,
      channelBindings: defaultChannelBindings,
      channelName: channel.name,
    },
    mode: 'onSubmit',
    resolver: zodResolver(schema),
    reValidateMode: 'onChange',
  });

  const { handleSubmit } = methods;

  return (
    <FormProvider
      {...methods}
      handleSubmit={handleSubmit as UseFormHandleSubmit<UpdateChannelFormState>}
    >
      {children}
    </FormProvider>
  );
};
